import { SearchPropertyLoadingSkeleton } from "@/components/Home/Common/PropertyList/Skeleton";
import { P } from "@/components/ui/Heading";
import { Separator } from "@/components/ui/Separator";
import { useSearch } from "@/contexts/SearchContext";
import { Locale } from "@/enums/common.enum";
import { useGetPropertyShowcase, useProperties } from "@/hooks/useProperty";
import { usePropertyView } from "@/hooks/usePropertyView";
import { makePropertyLink } from "@/lib/property/helpers";
import { convertStateToPropertyFilters } from "@/lib/property/listing/helpers";
import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import { useMemo } from "react";
import { EmptyPropertyList } from "./Empty";
import { PropertyList } from "./List";
import { ListFilters } from "./ListFilters";
import { ListPagination } from "./ListPagination";
import { ListTitle } from "./ListTitle";
import { PropertyListLoading } from "./Loading";
import { PropertyShowCaseCard } from "./PropertyShowCaseCard";

export interface PropertyListViewProps {
  variant?: "normal" | "slim";
}

export const PropertyListView = ({
  variant = "normal",
}: PropertyListViewProps) => {
  const locale = useLocale() as Locale;
  const t = useTranslations("shared");

  const { state } = useSearch();
  const filters = convertStateToPropertyFilters(state);

  const { data, isPending } = useProperties(filters);
  const { data: showcaseProperty, isPending: isShowcasePropertyLoading } =
    useGetPropertyShowcase(locale);

  const { handleCardClick, handleCardView, listingId } = usePropertyView(state);

  const showCaseProperty = useMemo(() => {
    return (
      variant === "normal" && (
        <div className={clsx("w-full hidden lg:block relative")}>
          <div className="sticky inset-0 top-32">
            <P className="uppercase text-xs mt-4 mb-2 font-semibold tracking-wider">
              {t(`words.property-showcase`)}
            </P>
            {isShowcasePropertyLoading ? (
              <SearchPropertyLoadingSkeleton />
            ) : (
              showcaseProperty && (
                <PropertyShowCaseCard
                  property={showcaseProperty}
                  slotProps={{
                    link: {
                      href: `${makePropertyLink(showcaseProperty, locale)}`,
                    },
                  }}
                />
              )
            )}
          </div>
        </div>
      )
    );
  }, [variant, showcaseProperty, isShowcasePropertyLoading]);

  const listContent = useMemo(() => {
    return isPending || !listingId ? (
      <PropertyListLoading />
    ) : (data?.items.length || 0) > 0 ? (
      <PropertyList
        id={listingId!}
        properties={data!.items!}
        variant={variant}
        onCardView={handleCardView}
        onCardClick={handleCardClick}
      />
    ) : (
      <EmptyPropertyList />
    );
  }, [isPending, data, listingId, variant, handleCardClick, handleCardView]);

  return (
    <>
      <Separator />
      <div
        className={clsx(
          "w-full max-w-screen-lg m-auto grid grid-cols-1 lg:grid-cols-[65%_35%] sm:px-4 gap-10",
          {
            "!px-0 ": variant === "slim",
          }
        )}
      >
        <div
          className={clsx("w-full lg:pr-4 space-y-2", {
            "lg:w-full !px-0 ": variant === "slim",
          })}
        >
          <ListTitle total={data?.meta?.totalItems} />
          <Separator />
          <ListFilters />
          {listContent}
          <div className="pt-4">
            <ListPagination data={data} />
          </div>
        </div>
        {showCaseProperty}
        {}
      </div>
    </>
  );
};
